import { useState } from 'react'
import { send as emailjsSend } from '@emailjs/browser'
import { toast } from 'react-toastify'
import EmailRegPattern from '@/config/EmailRegPattern'
import { ServicesID, ContactTemplateID, UserID } from '@/config/EmailJS'

interface EnquiryForm {
  fullName: string
  email: string
  phone: string
  dueDate: string
  services: string[]
  notes: string
}

const INITIAL_FORM: EnquiryForm = {
  fullName: '',
  email: '',
  phone: '',
  dueDate: '',
  services: [],
  notes: '',
}

const useEnquiryFormHook = () => {
  const [form, setForm] = useState<EnquiryForm>(INITIAL_FORM)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleServiceToggle = (service: string) => {
    setForm((prev) => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter((s) => s !== service)
        : [...prev.services, service],
    }))
  }

  const validate = (): string => {
    if (!form.fullName.trim() || !form.email.trim()) {
      return 'Full name and email are required'
    }
    if (form.fullName.trim().length < 3 || form.fullName.trim().length > 60) {
      return 'Name must be between 3 and 60 characters'
    }
    if (!EmailRegPattern.test(form.email)) {
      return 'Please enter a valid email address'
    }
    return ''
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const error = validate()
    if (error) {
      toast.error(error, { position: 'top-center' })
      return
    }

    setIsSubmitting(true)

    const emailData = {
      FROM_NAME: form.fullName.trim(),
      FROM_EMAIL: form.email.trim(),
      PHONE: form.phone.trim() || 'Not provided',
      DUE_DATE: form.dueDate || 'Not specified',
      SERVICES: form.services.length > 0 ? form.services.join(', ') : 'None selected',
      NOTES: form.notes.trim() || 'None',
      date: new Date().toLocaleString(),
    }

    emailjsSend(ServicesID, ContactTemplateID, emailData, UserID).then(
      () => {
        toast.success('Thank you! Simone will be in touch with you shortly.', {
          autoClose: 4000,
          position: 'top-center',
        })
        setForm(INITIAL_FORM)
        setSubmitted(true)
        setIsSubmitting(false)
      },
      () => {
        toast.error('Submission failed. Please try again later.', {
          autoClose: 4000,
          position: 'top-center',
        })
        setIsSubmitting(false)
      },
    )
  }

  return {
    form,
    isSubmitting,
    submitted,
    handleChange,
    handleServiceToggle,
    handleSubmit,
  }
}

export default useEnquiryFormHook
